<template>
  <vs-popup
    class="payment-settlement-tour-popup"
    :title="currentTitle"
    :active.sync="isActive"
  >
    <div class="tour-container">
      <!-- Stepper -->
      <div class="stepper">
        <div
          v-for="(step, index) in tourSteps"
          :key="index"
          class="step-item"
          :class="{
            active: index === activeStep,
            completed: index < activeStep,
          }"
        >
          <div class="step-circle">
            <span v-if="index < activeStep" class="check-icon">✓</span>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span class="step-label" :class="{ active: index === activeStep }">
            {{ step.shortTitle }}
          </span>
          <div
            v-if="index < tourSteps.length - 1"
            class="step-line"
            :class="{ completed: index < activeStep }"
          ></div>
        </div>
      </div>

      <div class="step-content text-center mt-5 mb-5">
        <div class="step-icon mb-4">
          {{ tourSteps[activeStep].icon }}
        </div>

        <h4 class="mb-3">{{ tourSteps[activeStep].title }}</h4>

        <p class="text-secondary description">
          {{ tourSteps[activeStep].description }}
        </p>
      </div>

      <div class="navigation-buttons flex justify-between items-center mt-5">
        <vs-button type="border" color="primary" @click="handleSkip">
          رد کردن
        </vs-button>

        <div class="flex gap-2">
          <vs-button
            type="border"
            @click="handleBack"
            :disabled="activeStep === 0"
            icon-pack="feather"
            icon="icon-arrow-right"
          >
            قبلی
          </vs-button>

          <vs-button
            @click="handleNext"
            icon-pack="feather"
            :icon="
              activeStep === tourSteps.length - 1
                ? 'icon-check'
                : 'icon-arrow-left'
            "
            icon-after
          >
            {{ activeStep === tourSteps.length - 1 ? "متوجه شدم" : "بعدی" }}
          </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import {
  markPaymentSettlementTourSeen,
  markPaymentSettlementTourCompleted,
} from "@/utils/tour-utils";

export default {
  name: "PaymentSettlementTour",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isFirstTime: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeStep: 0,
      tourSteps: [
        {
          shortTitle: "ثبت سفارش",
          title: "مرحله ۱: ثبت سفارش",
          description:
            "سفارش می‌تواند به دو صورت ثبت شود: یا مشتری سفارش خود را ثبت می‌کند که در این صورت با وضعیت 'در انتظار تایید' نمایش داده می‌شود، یا خود شما (سالندار مجموعه) سفارش را ثبت می‌کنید که در این صورت سفارشات داخلی به صورت خودکار تایید می‌شوند.",
          icon: "📝",
        },
        {
          shortTitle: "تایید سفارش",
          title: "مرحله ۲: تایید سفارش",
          description:
            "اگر سفارش توسط مشتری ثبت شده باشد، باید آن را تایید کنید و یک پیام برای مشتری ارسال می‌شود که سفارشش تایید شده است. اگر سفارش را خودتان ثبت کرده‌اید، به صورت خودکار تایید شده است. در هر دو حالت، سفارش باید تایید شده باشد.",
          icon: "✅",
        },
        {
          shortTitle: "دریافت پرداخت",
          title: "مرحله ۳: دریافت پرداخت از مشتری",
          description:
            "پس از آماده شدن سفارش و تحویل به مشتری، مشتری می‌تواند به صورت نقدی یا از طریق دستگاه پوز با کارت بانکی پرداخت را انجام دهد.",
          icon: "💳",
        },
        {
          shortTitle: "تسویه سیستم",
          title: "مرحله ۴: تسویه سفارش در سیستم",
          description:
            "بعد از دریافت پرداخت از مشتری، حتماً باید روی دکمه 'تسویه' کلیک کنید تا پرداخت در سیستم ثبت شود و سفارش به عنوان تسویه شده ثبت گردد.",
          icon: "💰",
        },
        {
          shortTitle: "نکته مهم",
          title: "⚠️ نکته مهم: اهمیت تسویه",
          description:
            "توجه کنید: اگر سفارشی را تایید کرده‌اید اما دکمه 'تسویه' را نزده‌اید، این سفارش در آمار فروش و آنالیز فروشگاه شما در نظر گرفته نمی‌شود. این موضوع می‌تواند باعث اشتباه در حساب‌کتاب‌های صندوق شما شود. همیشه بعد از دریافت پرداخت، حتماً دکمه تسویه را بزنید.",
          icon: "⚠️",
        },
      ],
    };
  },
  computed: {
    isActive: {
      get() {
        return this.open;
      },
      set(val) {
        if (!val) {
          this.$emit("close");
        }
      },
    },
    title() {
      return this.isFirstTime
        ? "🎉 راهنمای تسویه سفارشات پرداخت در محل"
        : "راهنمای تسویه سفارشات پرداخت در محل";
    },
    currentTitle() {
      return this.title;
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.activeStep = 0;
      }
    },
  },
  methods: {
    handleNext() {
      if (this.activeStep < this.tourSteps.length - 1) {
        this.activeStep++;
      } else {
        markPaymentSettlementTourCompleted();
        this.$emit("startTour"); // Reusing this event name from React code as completion callback
        this.$emit("close");
      }
    },
    handleBack() {
      if (this.activeStep > 0) {
        this.activeStep--;
      }
    },
    handleSkip() {
      markPaymentSettlementTourSeen();
      this.$emit("close");
    },
  },
};
</script>

<style scoped lang="scss">
.tour-container {
  padding: 10px;
}

.stepper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
  width: 100%;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  flex: 1;

  &:last-child {
    .step-line {
      display: none;
    }
  }
}

.step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;

  .step-item.active & {
    background-color: var(
      --vs-primary
    ); // Use vuesax primary color variable if available, or fallback
    background-color: #7367f0; // Fallback
    color: white;
  }

  .step-item.completed & {
    background-color: #28c76f;
    color: white;
  }
}

.step-label {
  font-size: 0.75rem;
  color: #b0b0b0;
  text-align: center;

  &.active {
    color: #000;
    font-weight: 600;
  }
}

.step-line {
  position: absolute;
  top: 14px;
  left: 50%;
  width: 100%;
  height: 2px;
  background-color: #e0e0e0;
  z-index: 1;

  &.completed {
    background-color: #28c76f;
  }
}

.step-icon {
  font-size: 3rem;
  display: flex;
  justify-content: center;
}

.description {
  font-size: 1rem;
  line-height: 1.6;
  color: #626262;
  text-align: justify;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
}

.gap-2 {
  gap: 0.5rem;
  display: flex;
}
</style>

